import { Fragment, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { Animate } from '@/components/common/Animate';
import { breakpoints, screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { getCurrentBreakpoint } from '@/lib/utils';

type BreakpointValues = {
  sm: number;
  md: number;
  lg: number;
  xl: number;
  max: number;
};

interface GridSystemProps {
  items: React.JSX.Element[];
  columns?: BreakpointValues;
  initialRows?: BreakpointValues;
  loadMoreRowIncrements?: BreakpointValues;
  loadMoreTrackingTag?: {
    _type: 'loadMoreTrackingTag';
    blockName: string;
    blockAction: string;
  };
}

export const ArticleGridSytem = ({
  columns = { sm: 1, md: 2, lg: 3, xl: 3, max: 3 },
  initialRows = { sm: 3, md: 2, lg: 2, xl: 2, max: 2 },
  loadMoreRowIncrements = { sm: 2, md: 1, lg: 1, xl: 1, max: 1 },
  items,
  loadMoreTrackingTag,
}: GridSystemProps) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState('max');
  const [loadMoreClicked, setLoadMoreClicked] = useState(false);

  const [displayItems, setDisplayItems] = useState([]);

  const showLoadMore = items.length > displayItems.length;

  useEffect(() => {
    const updateBreakpointOnResize = () => {
      const windowWidth = window.innerWidth;
      setCurrentBreakpoint(getCurrentBreakpoint(breakpoints, windowWidth));
    };
    window.addEventListener('resize', updateBreakpointOnResize);

    if (
      getCurrentBreakpoint(breakpoints, window.innerWidth) !== currentBreakpoint
    ) {
      setCurrentBreakpoint(
        getCurrentBreakpoint(breakpoints, window.innerWidth),
      );
    }

    if (!loadMoreClicked)
      setDisplayItems(
        items.slice(
          0,
          initialRows[currentBreakpoint] * columns[currentBreakpoint],
        ),
      );

    return () => {
      window.removeEventListener('resize', updateBreakpointOnResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBreakpoint]);

  const loadMore = () => {
    setLoadMoreClicked(true);
    setDisplayItems(
      items.slice(
        0,
        displayItems.length +
          loadMoreRowIncrements[currentBreakpoint] * columns[currentBreakpoint],
      ),
    );
  };

  return (
    <Container>
      <GridContainer columns={columns} showLoadMore={showLoadMore}>
        {displayItems.map((item, index) => {
          return index + 1 >
            initialRows[currentBreakpoint] * columns[currentBreakpoint] ? (
            <AnimateWrapper
              settings={{ scrollOffset: -10 }}
              key={item?.props?._key || index}
            >
              {item}
            </AnimateWrapper>
          ) : (
            <Fragment key={item?.props?._key || index}>{item}</Fragment>
          );
        })}
      </GridContainer>
      {showLoadMore && (
        <LoadMoreButton
          variant="flat-light"
          onClick={loadMore}
          className={
            loadMoreTrackingTag?.blockName &&
            `tracking-${loadMoreTrackingTag.blockName}__${loadMoreTrackingTag.blockAction}`
          }
        >
          Load more
        </LoadMoreButton>
      )}
    </Container>
  );
};
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AnimateWrapper = styled(Animate)`
  display: flex;
`;

const GridContainer = styled.div<{
  columns: BreakpointValues;
  showLoadMore: boolean;
}>`
  display: grid;
  width: 100%;
  gap: var(--spacing-small);
  grid-template-columns: ${({ columns }) => `repeat(${columns.sm}, 1fr)`};

  ${screen.sm} {
    grid-template-columns: ${({ columns }) => `repeat(${columns.md}, 1fr)`};
    gap: var(--spacing-300);
  }
  ${screen.md} {
    grid-template-columns: ${({ columns }) => `repeat(${columns.lg}, 1fr)`};
  }
  ${screen.lg} {
    grid-template-columns: ${({ columns }) => `repeat(${columns.xl}, 1fr)`};
  }
  ${screen.xl} {
    grid-template-columns: ${({ columns }) => `repeat(${columns.max}, 1fr)`};
  }
`;

const LoadMoreButton = styled(Button)`
  margin-top: var(--spacing-300);
`;
